/**
 * Who is looking at the metrics dashboard, as configuration: the Dashy platform team (every merchant,
 * /platform/dashboard) or one merchant on its own dashboard (/dashboard). Every admin/merchant
 * difference of the shell, the store, the API client and the shared tables is read from here; the
 * route provides MERCHANT_MODE, anything else gets PLATFORM_MODE (today's platform behaviour).
 *
 * Pure module: only type imports from the dashboard, so the store, the API client and the shared
 * components can all depend on it without import cycles.
 */
import { InjectionToken } from "@angular/core";
import type { MetricsSection } from "../../../shared/utils/merchant-dashboard-roles";
import type { CompareMode, DrillTarget, MetricsTab } from "./metrics-filter.store";

/**
 * View mode handed to the tabs: "platform" (all merchants) or "merchant" (one merchant: the merchant
 * dashboard, or the platform's company view). Merchant mode hides platform-only blocks: merchant picker,
 * per-merchant tables and leaderboards, platform averages and the Consumption view.
 */
export type MetricsMode = "platform" | "merchant";

/** Back-office API family: `/v1/api/admin-ui/metrics/**` (platform) or `/v1/api/ui/metrics/**` (merchant, JWT company). */
export type MetricsApiScope = "admin-ui" | "ui";

/** Views that only make sense across merchants. */
export const PLATFORM_ONLY_TABS = ["merchants", "consumption"] as const;

/** Drawers listing merchants (byCompany tables, leaderboards, churn, directory). */
export function isPlatformTarget(target: DrillTarget | null | undefined): boolean {
  if (!target) return false;
  if (target.tab && (PLATFORM_ONLY_TABS as readonly string[]).includes(target.tab)) return true;
  return !!target.drawer && /^(by-|leaderboard$|churn$|merchants$)/.test(target.drawer);
}

/** KPIs counting merchants ("Merchants with orders", users per merchant…). */
export function isPlatformKpi(id: string): boolean {
  return /\.(activeCompanies|usersPerMerchant)$/.test(id) || id.startsWith("COMPANIES.");
}

/**
 * Blocks a mode may leave out (never rendered, so nothing they alone need is requested):
 * - overview.modules: "Modules used" panel of the one-merchant overview;
 * - messaging.messageMix: message status / origin / type panel;
 * - payments.rateTrend / payments.cardMix: payment rates chart and card types panel;
 * - commerce.orderStats: secondary figures of the orders drawer (they repeat its cards).
 */
export type MetricsBlock = "overview.modules" | "messaging.messageMix" | "payments.rateTrend" | "payments.cardMix" | "commerce.orderStats";

/**
 * The METRICS_MERCHANT twin of a METRICS_ADMIN key the mode words differently (`texts`), else the key.
 * METRICS_ADMIN.HELP.MESSAGES.INBOUND → METRICS_MERCHANT.HELP.MESSAGES.INBOUND.
 */
export function modeText<T extends string | null | undefined>(mode: MetricsModeConfig, key: T): T {
  return (key && mode.texts.includes(key) ? key.replace(/^METRICS_ADMIN\./, "METRICS_MERCHANT.") : key) as T;
}

export interface MetricsModeConfig {
  kind: MetricsMode;
  apiScope: MetricsApiScope;
  /** Tabs of the shell, in order (capabilities may hide some of them). */
  tabs: readonly MetricsTab[];
  /** Merchant wording of some tab labels (i18n keys); the others keep the platform labels. */
  tabLabels: Partial<Record<MetricsTab, string>>;
  /** Sections a tab needs (any of): the tab shows when one of them is enabled. Empty/missing = always. */
  tabSections: Partial<Record<MetricsTab, readonly MetricsSection[]>>;
  /** Merchant picker, segments, `companies` / `p.scope` URL params, company view and Company 360. */
  companyFilter: boolean;
  /** WhatsApp number filter (`phones` URL param → waba `phoneReferences`). */
  phoneFilter: boolean;
  /** Load GET /ui/metrics/access and gate tabs and blocks on the sections it returns. */
  capabilities: boolean;
  /** Merchant cells open Company 360. */
  companyLinks: boolean;
  /** Column keys never shown in any table of the dashboard (the APIs drop them too). */
  hiddenColumns: readonly string[];
  /** Deep links to the back-office pages (orders, contacts, inbox…), shown with the target page's roles. */
  links: boolean;
  /** Hero / drawer KPI cards that may show. */
  kpiVisible: (id: string) => boolean;
  /** Drill targets (cards, alerts, mini-stats, URL drawers) that may open. */
  targetAllowed: (target: DrillTarget | null | undefined) => boolean;
  /**
   * Plain presentation for non-technical users: "Hello, <first name>" header with a one-line subtitle,
   * and drawers showing their list right under the cards (small breakdowns after it).
   */
  simple: boolean;
  /** Comparison of a first visit (no `compare` in the URL); "Today" still has none. */
  defaultCompare: CompareMode;
  /** "More filters" popover (granularity, timezone, segment) and its chips; without it the granularity is automatic. */
  advancedFilters: boolean;
  /** "Copy link" button of the filter bar. */
  copyLink: boolean;
  /** "Updated at … · auto-refresh every 15 min" with its refresh button, and the 15-minute auto-refresh. */
  autoRefresh: boolean;
  /** Blocks never rendered in this mode. */
  hiddenBlocks: readonly MetricsBlock[];
  /** Most KPI cards on top of a drawer (each figure once). */
  drawerKpis: number;
  /** METRICS_ADMIN keys shown with their METRICS_MERCHANT wording (see modeText). */
  texts: readonly string[];
}

/** The platform dashboard exactly as it is: every tab, merchant filters, no capability gating. */
export const PLATFORM_MODE: MetricsModeConfig = {
  kind: "platform",
  apiScope: "admin-ui",
  // ABB reconciliation (develop-prod) sits next to Payments; merchants do not get it
  tabs: ["overview", "merchants", "messaging", "campaigns", "contacts", "commerce", "payments", "reconciliation", "consumption", "team"],
  tabLabels: {},
  tabSections: {},
  companyFilter: true,
  phoneFilter: false,
  capabilities: false,
  companyLinks: true,
  hiddenColumns: [],
  links: false,
  kpiVisible: () => true,
  targetAllowed: () => true,
  simple: false,
  defaultCompare: "off",
  advancedFilters: true,
  copyLink: true,
  autoRefresh: true,
  hiddenBlocks: [],
  drawerKpis: 6,
  texts: [],
};

const MERCHANT_TABS: readonly MetricsTab[] = ["overview", "messaging", "campaigns", "contacts", "commerce", "payments", "team"];
/**
 * Drawers with no merchant endpoint: payments/operations and the company's own Dashy plan
 * (payments/subscriptions/summary reads company_subscriptions = Dashy billing).
 */
const MERCHANT_BLOCKED_DRAWERS = ["ops", "subscriptions"];

function merchantTarget(target: DrillTarget | null | undefined): boolean {
  if (!target || target.link) return true;
  if (isPlatformTarget(target)) return false;
  if (target.tab && !MERCHANT_TABS.includes(target.tab)) return false;
  return !target.drawer || !MERCHANT_BLOCKED_DRAWERS.includes(target.drawer);
}

/** Shared texts that speak of "merchants" or the platform: the merchant dashboard says "you" instead. */
const MERCHANT_TEXTS = [
  "METRICS_ADMIN.UPDATED_HELP",
  "METRICS_ADMIN.COMPANY_VIEW.ACTIVITY_TREND",
  "METRICS_ADMIN.HELP.MESSAGES.OUTBOUND",
  "METRICS_ADMIN.HELP.MESSAGES.INBOUND",
  "METRICS_ADMIN.HELP.MESSAGES.CONVERSATIONS_OPENED",
  "METRICS_ADMIN.HELP.CONTACTS.TOTAL",
  "METRICS_ADMIN.HELP.CONTACTS.ENGAGED",
  "METRICS_ADMIN.HELP.TEMPLATES.REQUESTED",
  "METRICS_ADMIN.HELP.BROADCASTS.BROADCASTS_CREATED",
  "METRICS_ADMIN.HELP.ORDERS.ORDERS",
  "METRICS_ADMIN.HELP.LINKS.CREATED",
  "METRICS_ADMIN.HELP.USERS.ACTIVE_USERS",
  "METRICS_ADMIN.HELP.TEAM.AGENTS",
  "METRICS_ADMIN.HELP.TEAM.ADMINS",
  "METRICS_ADMIN.FAILURE_GROUP_HELP.ACCOUNT_ISSUE",
  "METRICS_ADMIN.COMMERCE.CATALOG_HELP",
  "METRICS_ADMIN.CONTACTS.ORDER_ACTIVITY_HELP",
  "METRICS_ADMIN.TICKETS.GLANCE_HELP",
];

/** One merchant's dashboard (company from the JWT): built from the platform/merchant helpers above. */
export const MERCHANT_MODE: MetricsModeConfig = {
  kind: "merchant",
  apiScope: "ui",
  tabs: MERCHANT_TABS,
  tabLabels: {
    overview: "METRICS_MERCHANT.TAB.OVERVIEW",
    messaging: "METRICS_MERCHANT.TAB.CONVERSATIONS",
    campaigns: "METRICS_MERCHANT.TAB.CAMPAIGNS",
    contacts: "METRICS_MERCHANT.TAB.CONTACTS",
    commerce: "METRICS_MERCHANT.TAB.COMMERCE",
    payments: "METRICS_MERCHANT.TAB.PAYMENTS",
    team: "METRICS_MERCHANT.TAB.TEAM",
  },
  tabSections: {
    // the overview's cards and chart (catalogue, team and users alone leave it empty)
    overview: ["CONVERSATIONS", "CAMPAIGNS", "CONTACTS", "ORDERS", "PAYMENTS", "TICKETS"],
    messaging: ["CONVERSATIONS"],
    campaigns: ["CAMPAIGNS"],
    // customers alone (orders without contacts) sit in Sales: no tab holding a single panel
    contacts: ["CONTACTS"],
    commerce: ["ORDERS", "CATALOG"],
    payments: ["PAYMENTS"],
    team: ["TICKETS", "TEAM", "USERS"],
  },
  companyFilter: false,
  phoneFilter: true,
  capabilities: true,
  companyLinks: false,
  hiddenColumns: ["companyId", "companyName", "companies", "sharedWith"],
  links: true,
  kpiVisible: (id) => !isPlatformKpi(id) && !id.startsWith("SUBSCRIPTIONS."),
  targetAllowed: merchantTarget,
  simple: true,
  defaultCompare: "off",
  advancedFilters: false,
  copyLink: false,
  autoRefresh: false,
  hiddenBlocks: ["overview.modules", "payments.rateTrend", "payments.cardMix", "commerce.orderStats"],
  drawerKpis: 4,
  texts: MERCHANT_TEXTS,
};

/** Provided by the /dashboard route (MERCHANT_MODE); defaults to the platform dashboard. */
export const METRICS_MODE = new InjectionToken<MetricsModeConfig>("METRICS_MODE", {
  providedIn: "root",
  factory: () => PLATFORM_MODE,
});
